import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RotateCcw, RotateCw, ZoomIn, ZoomOut, RefreshCcw } from "lucide-react";

/** Render a cropped image (from a File URL) to a data URL. */
async function getCroppedDataUrl(imageSrc, crop, rotation = 0, outputSize = 512) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  // Draw rotated + cropped square onto canvas.
  const rad = (rotation * Math.PI) / 180;
  const maxSide = Math.max(image.width, image.height);
  const cvs = document.createElement("canvas");
  cvs.width = maxSide; cvs.height = maxSide;
  const ctx = cvs.getContext("2d");
  ctx.translate(maxSide / 2, maxSide / 2);
  ctx.rotate(rad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  // Crop region is in the original image coords produced by react-easy-crop AFTER rotation adjustment.
  const out = document.createElement("canvas");
  out.width = outputSize; out.height = outputSize;
  const octx = out.getContext("2d");
  octx.drawImage(
    cvs,
    (maxSide - image.width) / 2 + crop.x,
    (maxSide - image.height) / 2 + crop.y,
    crop.width, crop.height,
    0, 0, outputSize, outputSize,
  );
  return out.toDataURL("image/jpeg", 0.88);
}

export default function ImageCropModal({ open, onOpenChange, sourceUrl, onSave, testId = "crop-modal" }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => setCroppedArea(areaPixels), []);

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); };

  const confirm = async () => {
    if (!sourceUrl || !croppedArea) return;
    setSaving(true);
    try {
      const dataUrl = await getCroppedDataUrl(sourceUrl, croppedArea, rotation, 512);
      onSave(dataUrl);
      onOpenChange(false);
      reset();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>Edit your profile picture</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-72 bg-slate-100 rounded-md overflow-hidden">
          {sourceUrl && (
            <Cropper
              image={sourceUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} data-testid={`${testId}-rot-left`}>
            <RotateCcw className="w-4 h-4 mr-1" /> Rotate left
          </Button>
          <Button variant="outline" size="sm" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid={`${testId}-rot-right`}>
            <RotateCw className="w-4 h-4 mr-1" /> Rotate right
          </Button>
          <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.max(1, +(z - 0.1).toFixed(2)))} data-testid={`${testId}-zoom-out`}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)))} data-testid={`${testId}-zoom-in`}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={reset} data-testid={`${testId}-reset`}>
            <RefreshCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={confirm} disabled={saving} data-testid={`${testId}-save`} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
            {saving ? "Saving…" : "Save Profile Picture"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
